import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { api } from '~/lib/api-client';
import { isUserFacingRefusal } from '~/lib/query-client';
import {
  PRO_BUILDER_COPY_ANSWER_COPIED,
  PRO_BUILDER_COPY_ANSWER_FAILED,
  PRO_BUILDER_COPY_ANSWER_LABEL,
  PRO_BUILDER_COPY_ANSWER_NONE,
  PRO_BUILDER_COPY_ANSWER_TOO_LONG,
  PRO_BUILDER_COPY_ANSWER_TOOLTIP,
} from './pro-builder-copy';

/** What `GET /api/pro-builder/session/:id/answer` answers with a 200. */
export interface LastAnswerResponse {
  answer: string | null;
}

export type CopyOutcome = { kind: 'copied' } | { kind: 'none' } | { kind: 'too-long'; message: string } | { kind: 'error' };

/** The slice of `navigator.clipboard` the click uses. */
export interface ClipboardLike {
  write?: (items: unknown[]) => Promise<void>;
  writeText: (text: string) => Promise<void>;
}

type ClipboardItemCtor = new (items: Record<string, Promise<Blob>>) => unknown;

class NoAnswer extends Error {}

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

/** Maps a failed fetch onto the outcome the person is told about. */
function outcomeForFetchError(err: unknown): CopyOutcome {
  if (statusOf(err) === 404) return { kind: 'none' };
  if (statusOf(err) === 413) {
    const message = isUserFacingRefusal(err) && err instanceof Error && err.message ? err.message : PRO_BUILDER_COPY_ANSWER_TOO_LONG;
    return { kind: 'too-long', message };
  }
  return { kind: 'error' };
}

/**
 * The click's whole logic. Safari drops a click's user activation across an
 * awaited network call, so the clipboard write is STARTED synchronously here,
 * before any await, with a promise for the text: the browser waits for it.
 * That promise rejects when there is no answer or the fetch failed, so
 * nothing is ever written then. The outcome is decided from the fetch result,
 * never from the write's own rejection; when the write rejects but the text
 * arrived, `writeText` is the fallback.
 */
export async function copyLastAnswer(deps: {
  fetchAnswer: () => Promise<LastAnswerResponse>;
  clipboard: ClipboardLike;
  ClipboardItemCtor?: ClipboardItemCtor;
}): Promise<CopyOutcome> {
  const { fetchAnswer, clipboard, ClipboardItemCtor: Item } = deps;
  const answerRequest = (async () => fetchAnswer())();
  const blob = answerRequest.then((res) => {
    if (typeof res.answer !== 'string' || res.answer === '') throw new NoAnswer();
    return new Blob([res.answer], { type: 'text/plain' });
  });
  blob.catch(() => {});
  let write: Promise<void> | null = null;
  if (Item && typeof clipboard.write === 'function') {
    try {
      write = clipboard.write([new Item({ 'text/plain': blob })]);
      write.catch(() => {});
    } catch {
      write = null;
    }
  }
  let text: string;
  try {
    const res = await answerRequest;
    if (typeof res.answer !== 'string' || res.answer === '') return { kind: 'none' };
    text = res.answer;
  } catch (err) {
    return outcomeForFetchError(err);
  }
  if (write) {
    try {
      await write;
      return { kind: 'copied' };
    } catch {
      // fall through to writeText
    }
  }
  try {
    await clipboard.writeText(text);
    return { kind: 'copied' };
  } catch {
    return { kind: 'error' };
  }
}

/** The status line's words for an outcome, and how long they stay. */
export function copyStatusFor(outcome: CopyOutcome): { text: string; ms: number } {
  switch (outcome.kind) {
    case 'copied':
      return { text: PRO_BUILDER_COPY_ANSWER_COPIED, ms: 2500 };
    case 'none':
      return { text: PRO_BUILDER_COPY_ANSWER_NONE, ms: 5000 };
    case 'too-long':
      return { text: outcome.message, ms: 5000 };
    case 'error':
      return { text: PRO_BUILDER_COPY_ANSWER_FAILED, ms: 5000 };
  }
}

export function proBuilderAnswerPath(sessionId: string): string {
  return `/api/pro-builder/session/${encodeURIComponent(sessionId)}/answer`;
}

export function ProBuilderCopyAnswerButton({ sessionId }: { sessionId: string }) {
  const [status, setStatus] = useState<{ text: string; copied: boolean } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A copy can finish after the window unmounts; then neither the status nor its timer is set.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const onClick = () => {
    const outcomeP = copyLastAnswer({
      fetchAnswer: () => api.get<LastAnswerResponse>(proBuilderAnswerPath(sessionId)),
      clipboard: navigator.clipboard as unknown as ClipboardLike,
      ClipboardItemCtor: typeof ClipboardItem === 'undefined' ? undefined : (ClipboardItem as unknown as ClipboardItemCtor),
    });
    void outcomeP.then((outcome) => {
      if (!mounted.current) return;
      const { text, ms } = copyStatusFor(outcome);
      if (timer.current) clearTimeout(timer.current);
      setStatus({ text, copied: outcome.kind === 'copied' });
      timer.current = setTimeout(() => setStatus(null), ms);
    });
  };

  // The status sits in a small bubble under the button, outside the header's
  // flow: the header has no room for a sentence, and a clipped one says nothing.
  return (
    <span className="relative inline-flex">
      <span
        role="status"
        aria-live="polite"
        data-testid="pro-builder-copy-answer-status"
        className={
          status
            ? 'absolute right-0 top-full z-50 mt-1 w-max max-w-[280px] rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md'
            : 'sr-only'
        }
      >
        {status?.text ?? ''}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        tooltip={PRO_BUILDER_COPY_ANSWER_TOOLTIP}
        data-testid="pro-builder-copy-answer"
      >
        {status?.copied ? <Check size={16} weight="bold" aria-hidden /> : <Copy size={16} aria-hidden />}
        {PRO_BUILDER_COPY_ANSWER_LABEL}
      </Button>
    </span>
  );
}
